/**
 * Failure scenario catalogue for the simulation menu, from the simulator's
 * FAILURE_SCENARIOS (a record keyed by id) as a list with each stage's alert
 * level, message and numeric effects. The simulation session starts and
 * stops scenarios by the plain ids held in useTwin.failureScenarios.
 */
import { FAILURE_SCENARIOS as SIM_SCENARIOS, type FailureAlertLevel, type FailureScenario as SimScenario, type FailureStage } from '@/features/twin/sim/failureScenarios';

export type ScenarioAlert = FailureAlertLevel;

export interface ScenarioStage {
  alert: ScenarioAlert;
  message: string;
  /** The generator effects (fanSpeedDrop, tempRise, ...) and the modem status label. */
  effects: Record<string, number | string>;
}

export interface FailureScenario {
  id: string;
  name: string;
  description: string;
  affectedSensors: string[];
  stages: ScenarioStage[];
}

function stageEffects(stage: FailureStage): Record<string, number | string> {
  const effects: Record<string, number | string> = {};
  for (const [key, value] of Object.entries(stage)) {
    if (key === 'duration' || key === 'alert' || key === 'message') continue;
    if (typeof value === 'number' || typeof value === 'string') effects[key] = value;
  }
  return effects;
}

export function toCatalogueEntry(s: SimScenario): FailureScenario {
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    affectedSensors: s.affectedSensors,
    stages: s.stages.map((st) => ({ alert: st.alert, message: st.message, effects: stageEffects(st) })),
  };
}

export const FAILURE_SCENARIOS: FailureScenario[] = Object.values(SIM_SCENARIOS).map(toCatalogueEntry);

export function scenarioById(id: string): FailureScenario | undefined {
  return FAILURE_SCENARIOS.find((s) => s.id === id);
}
